import { Box, Grid, GridItem, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { getConcentrationConfigValueType } from '@finley-technologies/calc-agent';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { ConcentrationLimitCalculationGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { ValueDifferenceGridItem } from './BorrowingBaseSummaryValueDifferenceGridItem';
import {
  borrowingBaseSummaryFormatterMap,
  BorrowingBaseSummaryValueType,
  ExpandedConcentrationLimitCalculations,
  getConcentrationLimitConfigMapFromCreditAgreement,
  getDoesConcentrationLimitHaveFilters,
  getFormattedConcentrationLimit,
  getFormattedStringsForDeclarativeFilterOperations,
  getFormattedStringsForWeightedAverageFields,
  getNumberedSubRowLabel,
  getConcentrationLimitConfigCartsTemplateColumns,
  isDeclarativeFilterConcentrationLimitCalculationConfig,
  isPointerMethodCalculationConfig,
  isWeightedAverageConcentrationLimitCalculationConfig,
  BorrowingBaseSummaryInput,
  BorrowingBaseSummaryColumnValueInput,
  getConcentrationLimitRowTemplateColumns,
  getColumnValuesForLoanProductCalculationsField,
  LoanProductCalculationsBorrowingBaseSummaryInput,
  defaultBorrowingBaseSummaryColumnValueInput,
  AllBorrowingBaseReportSummaryFields,
  getExpandedConcentrationCalcs,
} from './utils';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { colors } from '~/packages/common-frontend/colors';

type ConcentrationConfigCardProps = {
  label: string;
  contentLines: string[] | undefined;
  concentrationLimitName: string; // used only for uniq child key
};
const ConcentrationConfigCard = ({
  label,
  contentLines,
  concentrationLimitName,
}: ConcentrationConfigCardProps) => {
  return (
    <GridItem
      borderRadius="8px"
      border={`1px solid ${colors.gray[200]}`}
      padding="16px"
      backgroundColor="white"
      colSpan={1}
    >
      <Text color={colors.black[70]} fontSize="14px">
        {label}
      </Text>
      {!!contentLines &&
        _.map(contentLines, (line, index) => (
          <Text
            key={`${concentrationLimitName}-${label}-${index}`}
            fontWeight={500}
            color={colors.black[95]}
            mt="8px"
          >
            {line ?? '-'}
          </Text>
        ))}
    </GridItem>
  );
};

type ConcentrationConfigCardsProps = {
  concentrationLimitConfig: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  historicalConcentrationLimit?: number;
};
export const ConcentrationConfigCards = ({
  concentrationLimitConfig,
  historicalConcentrationLimit,
}: ConcentrationConfigCardsProps) => {
  const concentrationLimitName = concentrationLimitConfig.name;

  const concentrationLimitCalculationConfig =
    concentrationLimitConfig._config_.concentrationLimitConfig
      ?.calculationConfig;

  const isPointerMethod = isPointerMethodCalculationConfig(
    concentrationLimitCalculationConfig,
  );
  const isDeclarativeFilterCalculationConfig = isDeclarativeFilterConcentrationLimitCalculationConfig(
    concentrationLimitCalculationConfig,
  );
  const isWeightedAverageCalculationConfig = isWeightedAverageConcentrationLimitCalculationConfig(
    concentrationLimitCalculationConfig,
  );
  const hasFilters = getDoesConcentrationLimitHaveFilters(
    concentrationLimitConfig,
  );

  const formattedConcentrationLimitString = getFormattedConcentrationLimit(
    concentrationLimitConfig,
    historicalConcentrationLimit,
  );
  const concentrationReceivablesStrings = isDeclarativeFilterCalculationConfig
    ? getFormattedStringsForDeclarativeFilterOperations(
        concentrationLimitCalculationConfig,
      )
    : undefined;
  const weightedAverageReceivableStrings = isWeightedAverageCalculationConfig
    ? getFormattedStringsForWeightedAverageFields(
        concentrationLimitCalculationConfig,
      )
    : undefined;

  const templateColumns = getConcentrationLimitConfigCartsTemplateColumns({
    isWeightedAverage: isWeightedAverageCalculationConfig,
    hasFilters,
  });

  return (
    <Grid templateColumns={templateColumns} gap="16px" marginBottom="16px">
      <ConcentrationConfigCard
        label="Concentration Limit"
        contentLines={
          formattedConcentrationLimitString
            ? [formattedConcentrationLimitString]
            : undefined
        }
        concentrationLimitName={concentrationLimitName}
      />
      {isPointerMethod && (
        <ConcentrationConfigCard
          label="Concentration Configuration"
          contentLines={['This is a custom concentration method.']}
          concentrationLimitName={concentrationLimitName}
        />
      )}
      {hasFilters && (
        <ConcentrationConfigCard
          label="Concentration Receivables"
          contentLines={concentrationReceivablesStrings}
          concentrationLimitName={concentrationLimitName}
        />
      )}
      {isWeightedAverageCalculationConfig && (
        <ConcentrationConfigCard
          label="Weighted Average Field"
          contentLines={weightedAverageReceivableStrings}
          concentrationLimitName={concentrationLimitName}
        />
      )}
    </Grid>
  );
};

type BorrowingBaseSummaryConcentrationLimitRowProps = {
  label: string;
  columnValues: BorrowingBaseSummaryColumnValueInput;
  valueType: BorrowingBaseSummaryValueType;
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
  fieldName: AllBorrowingBaseReportSummaryFields;
};
const BorrowingBaseSummaryConcentrationLimitRow = ({
  label,
  columnValues,
  valueType,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
  fieldName,
}: BorrowingBaseSummaryConcentrationLimitRowProps) => {
  const formatter = borrowingBaseSummaryFormatterMap[valueType];

  // value difference calculation and formatting
  const minuend = columnValues.minuendValue;
  const subtrahend = columnValues.subtrahendValue;

  return (
    <>
      {/* If only a single column of values: */}
      {!showComparisonAndDifferenceColumns &&
        !showValueAndReferenceColumnsOnly && (
          <GridItem colSpan={2}>
            {label}
            <Box display="inline-block" ml="4px">
              <CalculationTerminologiesPopover termKeyLabel={label} />
            </Box>
          </GridItem>
        )}
      {!showComparisonAndDifferenceColumns &&
        !showValueAndReferenceColumnsOnly && (
          <GridItem colSpan={1}>
            <Text>{formatter(columnValues.minuendValue)}</Text>
          </GridItem>
        )}

      {/* If showing value and reference columns only: */}
      {showValueAndReferenceColumnsOnly && (
        <GridItem colSpan={2}>
          {label}
          <Box display="inline-block" ml="4px">
            <CalculationTerminologiesPopover termKeyLabel={label} />
          </Box>
        </GridItem>
      )}
      {showValueAndReferenceColumnsOnly && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.referenceValue)}
          </Text>
        </GridItem>
      )}
      {showValueAndReferenceColumnsOnly && (
        <GridItem colSpan={1}>
          <Text>{formatter(columnValues.minuendValue)}</Text>
        </GridItem>
      )}

      {/* If showing all columns of comparison and difference values: */}
      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1}>
          {label}
          <Box display="inline-block" ml="4px">
            <CalculationTerminologiesPopover termKeyLabel={label} />
          </Box>
        </GridItem>
      )}

      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.referenceValue)}
          </Text>
        </GridItem>
      )}
      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.subtrahendValue)}
          </Text>
        </GridItem>
      )}
      {showComparisonAndDifferenceColumns && (
        <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
          <Text fontSize="16px" textAlign="left" color={colors.black[70]}>
            {formatter(columnValues.minuendValue)}
          </Text>
        </GridItem>
      )}
      {showComparisonAndDifferenceColumns && (
        // Difference column with special formatting
        <ValueDifferenceGridItem
          subtrahend={subtrahend}
          minuend={minuend}
          valueType={valueType}
          fieldName={fieldName}
        />
      )}
      {showComparisonAndDifferenceColumns && (
        // Placeholder to align with the above accordion dropdown icon
        <GridItem colSpan={1} />
      )}
    </>
  );
};

type BorrowingBaseSummaryConcentrationLimitProps = {
  concentrationLimitConfig?: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  expandedConcentrationLimitCalculations?: {
    minuend?: ExpandedConcentrationLimitCalculations;
    subtrahend?: ExpandedConcentrationLimitCalculations;
    reference?: ExpandedConcentrationLimitCalculations;
  };
  totalConcentrationKickout: {
    minuend?: number;
    subtrahend?: number;
    reference?: number;
  };
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
};
export const BorrowingBaseSummaryConcentrationLimit = ({
  concentrationLimitConfig,
  expandedConcentrationLimitCalculations,
  totalConcentrationKickout,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
}: BorrowingBaseSummaryConcentrationLimitProps) => {
  if (!concentrationLimitConfig || !expandedConcentrationLimitCalculations) {
    return null;
  }

  const config = concentrationLimitConfig?._config_.concentrationLimitConfig;
  const concentrationLimitValueType = getConcentrationConfigValueType(config);

  return (
    <Box>
      <ConcentrationConfigCards
        concentrationLimitConfig={concentrationLimitConfig}
        historicalConcentrationLimit={
          expandedConcentrationLimitCalculations.minuend?.concentrationLimit
        }
      />
      <Grid
        templateColumns={getConcentrationLimitRowTemplateColumns({
          showComparisonAndDifferenceColumns,
          showValueAndReferenceColumnsOnly,
        })}
        gap="16px"
        marginBottom="16px"
      >
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration Amount"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend
                ?.concentrationAmount,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend
                ?.concentrationAmount,
            referenceValue:
              expandedConcentrationLimitCalculations.reference
                ?.concentrationAmount,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="concentrationAmount"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Total Eligible Exposure Less Concentration Kickouts"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend
                ?.totalEligibleExposureLessConcentrationKickouts,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend
                ?.totalEligibleExposureLessConcentrationKickouts,
            referenceValue:
              expandedConcentrationLimitCalculations.reference
                ?.totalEligibleExposureLessConcentrationKickouts,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="totalEligibleExposureLessConcentrationKickouts"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend?.concentration,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend?.concentration,
            referenceValue:
              expandedConcentrationLimitCalculations.reference?.concentration,
          }}
          valueType={
            concentrationLimitValueType === 'number'
              ? 'decimal'
              : concentrationLimitValueType
          }
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="concentration"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration % Denominator"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend
                ?.totalEligibleExposure,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend
                ?.totalEligibleExposure,
            referenceValue:
              expandedConcentrationLimitCalculations.reference
                ?.totalEligibleExposure,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="totalEligibleExposure"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration Limit Amount"
          columnValues={{
            minuendValue:
              expandedConcentrationLimitCalculations.minuend
                ?.concentrationLimitAmount,
            subtrahendValue:
              expandedConcentrationLimitCalculations.subtrahend
                ?.concentrationLimitAmount,
            referenceValue:
              expandedConcentrationLimitCalculations.reference
                ?.concentrationLimitAmount,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="concentrationLimitAmount"
        />
        <BorrowingBaseSummaryConcentrationLimitRow
          label="Concentration Kickout"
          columnValues={{
            minuendValue: totalConcentrationKickout.minuend,
            subtrahendValue: totalConcentrationKickout.subtrahend,
            referenceValue: totalConcentrationKickout.reference,
          }}
          valueType="currency"
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="totalConcentrationKickout"
        />
      </Grid>
    </Box>
  );
};

type BorrowingBaseSummaryConcentrationLimitAccordionProps = {
  loanProductCalculations: LoanProductCalculationsBorrowingBaseSummaryInput;
  borrowingBaseReports: BorrowingBaseSummaryInput;
  concentrationLimitCalculationsForLoanProduct: {
    minuend: ConcentrationLimitCalculationGQLType[];
    subtrahend?: ConcentrationLimitCalculationGQLType[];
    reference?: ConcentrationLimitCalculationGQLType[];
  };
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  loanProductId?: string;
  loanProductLabel: string;
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
  defaultIsExpanded: boolean;
};
export const BorrowingBaseSummaryConcentrationLimitAccordion = ({
  loanProductCalculations,
  creditAgreement,
  borrowingBaseReports,
  concentrationLimitCalculationsForLoanProduct,
  loanProductId,
  loanProductLabel,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
  defaultIsExpanded,
}: BorrowingBaseSummaryConcentrationLimitAccordionProps) => {
  const { useFrontendWaterfallWorkaround } = useFeatureFlags();
  const concentrationLimitConfigMap = getConcentrationLimitConfigMapFromCreditAgreement(
    creditAgreement,
  );

  const totalConcentrationKickoutForLps = loanProductId
    ? getColumnValuesForLoanProductCalculationsField({
        loanProductCalculationsForBorrowingBaseReports: loanProductCalculations,
        loanProductId,
        fieldName: 'totalConcentrationKickout',
      })
    : defaultBorrowingBaseSummaryColumnValueInput;

  const hasConcentrationLimitCalculationsForLoanProduct =
    !!concentrationLimitCalculationsForLoanProduct.minuend.length ||
    !!concentrationLimitCalculationsForLoanProduct.subtrahend?.length ||
    !!concentrationLimitCalculationsForLoanProduct.reference?.length;

  return (
    <Box>
      {!hasConcentrationLimitCalculationsForLoanProduct && (
        <BorrowingBaseSummaryAccordion
          accordionButton={({ hasAccordionPanel, isExpanded }) => (
            <BorrowingBaseSummaryAccordionRow
              label={loanProductLabel}
              columnValues={totalConcentrationKickoutForLps}
              valueType="currency"
              hasAccordionPanel={hasAccordionPanel}
              isExpanded={isExpanded}
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
              showValueAndReferenceColumnsOnly={
                showValueAndReferenceColumnsOnly
              }
              fieldName="totalConcentrationKickout"
            />
          )}
        />
      )}

      {hasConcentrationLimitCalculationsForLoanProduct && (
        <BorrowingBaseSummaryAccordion
          defaultIsExpanded={defaultIsExpanded}
          accordionButton={({ hasAccordionPanel, isExpanded }) => (
            <BorrowingBaseSummaryAccordionRow
              label={loanProductLabel}
              columnValues={totalConcentrationKickoutForLps}
              valueType="currency"
              hasAccordionPanel={hasAccordionPanel}
              isExpanded={isExpanded}
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
              showValueAndReferenceColumnsOnly={
                showValueAndReferenceColumnsOnly
              }
              fieldName="totalConcentrationKickout"
            />
          )}
          accordionPanel={
            <>
              {getExpandedConcentrationCalcs({
                borrowingBaseReports,
                concentrationLimitCalcs: concentrationLimitCalculationsForLoanProduct,
                concentrationLimitConfigMap,
                useWaterfallExposure:
                  useFrontendWaterfallWorkaround &&
                  creditAgreement.borrowingBaseMechanics.calcAgentMechanics
                    ._config_.concentrationKickoutMethod === 'waterfall',
              }).map(({ concentrationLimitKey, expandedCalcs }, index) => {
                const { minuend, subtrahend, reference } = expandedCalcs;
                const concentrationLimitConfig = concentrationLimitConfigMap.get(
                  concentrationLimitKey,
                );

                const concentrationLimitKeyLabel = getNumberedSubRowLabel(
                  index,
                  concentrationLimitKey,
                );

                let columnValues: {} = {
                  minuendValue: minuend?.borrowingBaseReduction,
                };

                if (showComparisonAndDifferenceColumns) {
                  columnValues = {
                    minuendValue: minuend?.borrowingBaseReduction,
                    subtrahendValue: subtrahend?.borrowingBaseReduction,
                    referenceValue: reference?.borrowingBaseReduction,
                  };
                }

                if (showValueAndReferenceColumnsOnly) {
                  columnValues = {
                    minuendValue: minuend?.borrowingBaseReduction,
                    referenceValue: reference?.borrowingBaseReduction,
                  };
                }

                return (
                  <Grid
                    templateColumns="repeat(5, 1fr)"
                    gap="16px"
                    key={`concentration-limit-accordion-${concentrationLimitKey}`}
                  >
                    <GridItem colSpan={5}>
                      <BorrowingBaseSummaryAccordion
                        accordionButton={({
                          hasAccordionPanel,
                          isExpanded,
                        }) => (
                          <BorrowingBaseSummaryAccordionRow
                            label={concentrationLimitKeyLabel}
                            columnValues={columnValues}
                            valueType="currency"
                            hasAccordionPanel={hasAccordionPanel}
                            isExpanded={isExpanded}
                            showComparisonAndDifferenceColumns={
                              showComparisonAndDifferenceColumns
                            }
                            showValueAndReferenceColumnsOnly={
                              showValueAndReferenceColumnsOnly
                            }
                            fieldName="totalConcentrationKickout"
                          />
                        )}
                        accordionPanel={
                          <BorrowingBaseSummaryConcentrationLimit
                            concentrationLimitConfig={concentrationLimitConfig}
                            expandedConcentrationLimitCalculations={
                              expandedCalcs
                            }
                            totalConcentrationKickout={{
                              minuend:
                                expandedCalcs.minuend?.borrowingBaseReduction,
                              subtrahend:
                                expandedCalcs.subtrahend
                                  ?.borrowingBaseReduction,
                              reference:
                                expandedCalcs.reference?.borrowingBaseReduction,
                            }}
                            showComparisonAndDifferenceColumns={
                              showComparisonAndDifferenceColumns
                            }
                            showValueAndReferenceColumnsOnly={
                              showValueAndReferenceColumnsOnly
                            }
                          />
                        }
                      />
                    </GridItem>
                  </Grid>
                );
              })}
            </>
          }
        />
      )}
    </Box>
  );
};
